/**
 * @file Phone Input component.
 */

import React, { useState, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import TelephoneInput from 'react-telephone-input';
import InputMask from 'react-input-mask';
import classNames from 'classnames';

import Label from './Label';
import { getAsset } from '../../util/assets';

const propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  label: PropTypes.string,
  labelNote: PropTypes.string,
  labelSrOnly: PropTypes.bool,
  labelType: PropTypes.oneOf(['standard', 'expanded', 'light']),
  disabled: PropTypes.bool,
  onPhoneCodeChange: PropTypes.func,
  size: PropTypes.oneOf(['sm']),
  telephoneInputProps: PropTypes.object,
};

const PhoneInput = ({
  id,
  value,
  autoFocus,
  className,
  label,
  labelNote,
  disabled,
  size,
  onChange,
  labelSrOnly,
  labelType,
  onPhoneCodeChange,
  telephoneInputProps,
  ...props
}) => {
  const [maskedPhoneCode, setMaskedPhoneCode] = useState('+1');
  const maskRef = useRef(null);
  const phoneInputRef = useRef(null);

  const updatePhone = event => {
    onChange((event.target.value ?? '').replace(/[^+\d]/g, ''));
  };

  const updatePhoneCode = (phone, country) => {
    setMaskedPhoneCode(`+${country.dialCode.replace(/9/g, '\\9')}`);
    onChange(phone, country);
  };

  useEffect(() => {
    const guessedCountry = phoneInputRef.current?.state?.selectedCountry;

    if (guessedCountry) {
      updatePhoneCode(value, guessedCountry);
    }
  }, [phoneInputRef.current]);

  useEffect(() => {
    if (maskRef.current?.input && autoFocus) {
      maskRef.current.input.setSelectionRange(value.length, value.length);
      maskRef.current.input.focus();
    }
  }, [maskRef.current?.input]);

  useEffect(() => {
    onPhoneCodeChange?.(maskedPhoneCode);
  }, [maskedPhoneCode]);

  const classes = classNames({
    'form-group': true,
    [className]: !!className,
  });

  const telephoneInputClasses = classNames({
    'phone-input': true,
    'phone-input-sm': size === 'sm',
  });

  const inputMaskClasses = classNames({
    'phone-input-mask form-control': true,
    'form-control-sm': size === 'sm',
  });

  const mask = `${maskedPhoneCode}999999999999999`;

  return (
    <div className={classes}>
      {label && (
        <Label
          id={id}
          label={label}
          labelNote={labelNote}
          labelSrOnly={labelSrOnly}
          labelType={labelType}
        />
      )}

      <TelephoneInput
        autoFormat={false}
        classNames={telephoneInputClasses}
        defaultCountry="us"
        flagsImagePath={getAsset('/phone-input/flags.png')}
        onChange={updatePhoneCode}
        value={value}
        preferredCountries={['us']}
        // Force the component to properly format the value
        // on initial mount by focusing on it
        inputProps={{
          autoFocus: true,
        }}
        onFocus={updatePhoneCode}
        disabled={disabled}
        wrappedRef={ref => {
          phoneInputRef.current = ref;
        }}
        {...(telephoneInputProps ?? {})}
      />
      <InputMask
        id={id}
        name="phone"
        className={inputMaskClasses}
        mask={mask}
        maskChar={null}
        value={value}
        onChange={updatePhone}
        disabled={disabled}
        data-testid="input-mask"
        ref={maskRef}
        autoComplete="tel"
        {...props}
      />
    </div>
  );
};

PhoneInput.propTypes = propTypes;

export default memo(PhoneInput);
